<template>
  <div class="app-wrapper">
    <!-- 横竖屏提示 -->
    <OrientationTip />

    <!-- 开屏头图区域（独立层） -->
    <Header
      @gotoLogin="gotoLogin"
      @logout="handleLogout"
      @bindRole="handleBindRole"
      @switchRole="handleSwitchRole"
      @reserve="handleReserve"
      @showRulePopup="showRulePopup"
      @jumpToFacebook="jumpToFacebook"
      @jumpToDiscord="jumpToDiscord"
    />

    <!-- 主要内容容器 -->
    <div class="main-container">
      <!-- 里程碑板块 -->
      <section class="milestone-section">
        <Milestone />
      </section>

      <!-- 组队邀请板块 -->
      <section class="team-invite-section">
        <TeamInvite />
      </section>

      <!-- 抽奖和任务板块（合并） -->
      <section class="lottery-section">
        <div class="lottery-and-task">
          <div class="lottery-and-task-title"></div>
          <div class="lottery-and-task-content">
            <!-- 左侧：抽奖机 -->
            <div class="lottery-machine-wrapper">
              <Lottery />
            </div>
            <!-- 右侧：任务 -->
            <div class="task-wrapper">
              <Task />
            </div>
          </div>
        </div>
      </section>

      <!-- 版本内容板块 -->
      <section class="version-content-section" ref="versionContentRef">
        <div class="version-content"></div>
      </section>
    </div>

    <!-- Footer -->
    <Footer />

    <!-- 侧边导航 -->
    <Sidebar />

    <!-- 抽奖成功弹窗 -->
    <V3LotterySuccPopup v-show="v3PopupState.lotterySuccPopupState" />

    <!-- 我的奖品弹窗 -->
    <V3BagPopup v-show="v3PopupState.bagPopupState" />

    <!-- 奖池信息弹窗 -->
    <V3PoolPopup v-show="v3PopupState.poolPopupState" />

    <!-- 积分明细弹窗 -->
    <V3ScoreDetailPopup v-show="v3PopupState.scoreDetailPopupState" />

    <!-- 礼包码弹窗 -->
    <AwardCodePopup />

    <!-- 抽奖结果弹窗 -->
    <LotteryResultPopup />

    <!-- 提示弹窗 -->
    <V3TipPopup
      :visible="tipPopupState.visible"
      :message="tipPopupState.message"
      @close="tipPopupState.visible = false"
      @update:visible="tipPopupState.visible = $event"
    />

    <!-- 规则弹窗 -->
    <RulePopup
      :visible="rulePopupVisible"
      :content="rulePopupContent"
      @update:visible="rulePopupVisible = $event"
    />

    <!-- 绑定角色弹窗 -->
    <BindRolePopup
      :visible="bindRolePopupVisible"
      :platform-id="'global'"
      :activity-name="'row_reservation_01'"
      :game-id="386"
      :allow-new-server-ids="allowNewServerIds"
      :current-bind-info="currentBindInfo"
      @update:visible="bindRolePopupVisible = $event"
      @bind-success="handleBindSuccess"
      @bind-failed="handleBindFailed"
    />

    <!-- 视频弹窗 -->
    <VideoPopup
      :visible="videoPopupVisible"
      :video-url="videoUrl"
      @update:visible="videoPopupVisible = $event"
    />

    <!-- 创建弹窗 -->
    <CreatePopup
      :visible="createPopupVisible"
      @update:visible="createPopupVisible = $event"
      @confirm="handleCreateConfirm"
      @cancel="handleCreateCancel"
    />

    <!-- 加入队伍弹窗 -->
    <JoinTeamPopup
      :visible="joinTeamPopupVisible"
      :error="joinTeamError"
      :has-invite-code="joinTeamHasInviteCode"
      :invite-role-name="inviteRoleName"
      :invite-code="inviteCode"
      @update:visible="joinTeamPopupVisible = $event"
      @confirm="handleJoinTeamConfirm"
      @cancel="handleJoinTeamCancel"
    />

    <!-- 队伍列表弹窗 -->
    <TeamListPopup
      :visible="teamListPopupVisible"
      :team-members="teamMembers"
      @update:visible="teamListPopupVisible = $event"
    />

    <!-- 分享弹窗 -->
    <SharePopup v-model="sharePopupVisible" :mode="shareMode" />
  </div>
</template>

<script setup>
import { ref, provide, onMounted, reactive, computed } from 'vue';
import OrientationTip from './components/OrientationTip.vue';
import Header from './components/Header.vue';
import Milestone from './components/Milestone.vue';
import TeamInvite from './components/TeamInvite.vue';
import Lottery from './components/Lottery.vue';
import Task from './components/Task.vue';
import Sidebar from './components/Sidebar.vue';
import Footer from './components/Footer.vue';
import V3LotterySuccPopup from './components/popup/V3LotterySuccPopup.vue';
import V3BagPopup from './components/popup/V3BagPopup.vue';
import V3PoolPopup from './components/popup/V3PoolPopup.vue';
import V3ScoreDetailPopup from './components/popup/V3ScoreDetailPopup.vue';
import V3TipPopup from './components/popup/V3TipPopup.vue';
import RulePopup from './components/popup/RulePopup.vue';
import BindRolePopup from './components/popup/BindRolePopup.vue';
import VideoPopup from './components/popup/VideoPopup.vue';
import CreatePopup from './components/popup/CreatePopup.vue';
import JoinTeamPopup from './components/popup/JoinTeamPopup.vue';
import TeamListPopup from './components/popup/TeamListPopup.vue';
import SharePopup from './components/popup/SharePopup.vue';
import AwardCodePopup from './components/popup/AwardCodePopup.vue';
import LotteryResultPopup from './components/popup/LotteryResultPopup.vue';
import { useLottery } from '@/hooks/useLottery';
import { useTask } from '@/hooks/useTask';
import { useTeamInvite } from '@/hooks/useTeamInvite';
import { getLogoutApi, getActivityApi, point, initGlobalAdPoint } from '@/utils';
import { getJSON } from '@/api/request';
import { useRulePopup } from '@/hooks/usePopup';
import { useDebounceLock } from '@/hooks/useDebounce';
import language from '@/hooks/language';
import useViewPagePoint from '@/hooks/useViewPagePoint';

// 初始化语言
const { getLangTxt } = language();

// 弹窗缩放处理
const applyPopupScale = (scale) => {
  const style = document.createElement('style');
  style.id = 'dynamic-popup-scale';

  // 移除旧的样式
  const oldStyle = document.getElementById('dynamic-popup-scale');
  if (oldStyle) {
    oldStyle.remove();
  }

  style.textContent = `
    .popup-content,
    .popup-close-btn {
      transform: translate(-50%, -50%) scale(${scale}) !important;
    }
    .popup-content {
      overflow: hidden !important;
    }
    .v3-popup-content {
      transform: translate(-50%, -50%) scale(${scale}) !important;
      overflow: hidden !important;
    }
    .v3-popup-content.large {
      transform: translate(-50%, -50%) scale(${scale}) !important;
      overflow: hidden !important;
    }
    .v3-popup-content.small {
      transform: translate(-50%, -50%) scale(${scale}) !important;
      overflow: hidden !important;
    }
    .v3-popup-content.succ {
      transform: translate(-50%, -50%) scale(${scale}) !important;
      overflow: hidden !important;
    }
    /* 视频弹窗缩放 */
    .video-popup-content {
      transform: scale(${scale}) !important;
      overflow: hidden !important;
    }
    /* 修正关闭按钮位置 - 需要根据缩放调整 margin */
    .popup-close-btn {
      margin-top: calc((-801px / 2 - 20px) * ${scale}) !important;
      margin-left: calc((1052px / 2 - 20px) * ${scale}) !important;
    }
    .v3-popup-content-close {
      transform: scale(${1 / scale}) !important;
      transform-origin: top right;
    }
  `;

  document.head.appendChild(style);
};

// 版本内容页视图打点
const { targetRef: versionContentRef } = useViewPagePoint('版本内容页');

// ========== 提示弹窗 ==========
const tipPopupState = reactive({
  visible: false,
  message: '',
});

// 重写 showTipMessage，使用弹窗而不是 alert
const showTipMessageOverride = (msg) => {
  tipPopupState.message = msg;
  tipPopupState.visible = true;
};

// 重写 showCurErrorTips，使用弹窗而不是 alert
const showCurErrorTipsOverride = (code) => {
  // 使用 getLangTxt 从语言文件获取错误信息，如果没有则使用通用提示
  const message = getLangTxt(code, getLangTxt('common'));
  // 只有当 code 存在时才添加错误码前缀
  tipPopupState.message = code ? `【${code}】${message}` : message;
  tipPopupState.visible = true;
};

// ========== 组队功能 ==========
const {
  state: teamInviteState,
  hasTeam,
  initTeamInfo,
  triggerCreateTeam,
  joinOtherTeam,
  getTeamMembers,
} = useTeamInvite({
  platformId: 'global',
  activityName: 'row_reservation_01',
  gameId: 386,
});

// ========== 抽奖功能 ==========
const {
  isReady,
  isLogin,
  accountName,
  isBind,
  roleName,
  sname,
  isReserved,
  inviteCode,
  myCode,
  inviteRoleName,
  membersCount, // 队伍人数
  serverTimestamp,
  v3PopupState,
  lotteryResult,
  lotteryAndTaskState,
  taskList, // 任务列表
  checkLogin,
  gotoLogin,
  initActivity,
  refreshTaskStatus, // 刷新任务状态
  init: initLottery,
} = useLottery({
  platformId: 'global',
  activityName: 'row_reservation_01',
  gameId: 386,
  // 当 basic 数据加载完成后，如果有 teamCode，则初始化队伍信息
  onBasicDataLoaded: (data) => {
    if (data?.teamCode) {
      initTeamInfo(data);
    }
  },
});

// ========== 任务功能 ==========
const { claimTaskReward } = useTask({
  platformId: 'global',
  activityName: 'row_reservation_01',
});

// ========== 规则弹窗 ==========
const { rulePopupVisible, rulePopupContent, showRulePopup } = useRulePopup();

// ========== 绑定角色弹窗 ==========
const bindRolePopupVisible = ref(false);
const allowNewServerIds = ref([]); // 允许的新服ID列表

// 当前绑定信息（用于切换角色时回显）
const currentBindInfo = computed(() => {
  if (!isBind.value) {
    return null;
  }

  // 从 basic 接口返回的 bindInfo 构造绑定信息
  // 注意：basic 接口返回的数据已经在 useLottery 中处理并存储
  return {
    serverId: lotteryAndTaskState.bindInfo?.serverId || null,
    serverName: sname.value || '',
    sId: lotteryAndTaskState.bindInfo?.sId || null,
    roleId: lotteryAndTaskState.bindInfo?.roleId || null,
    roleName: roleName.value || '',
  };
});

// ========== 视频弹窗 ==========
const videoPopupVisible = ref(false);
const videoUrl = ref(''); // 视频地址

// ========== 创建弹窗 ==========
const createPopupVisible = ref(false);

// ========== 加入队伍弹窗 ==========
const joinTeamPopupVisible = ref(false);
const joinTeamError = ref('');
const joinTeamHasInviteCode = ref(false); // 是否为邀请模式

// ========== 队伍列表弹窗 ==========
const teamListPopupVisible = ref(false);
const teamMembers = ref([]);

// ========== 分享弹窗 ==========
const sharePopupVisible = ref(false);
const shareMode = ref('daily'); // 'daily': 普通分享, 'team_invite': 组队邀请

// 打开分享弹窗
const showSharePopup = (mode = 'daily') => {
  shareMode.value = mode;
  sharePopupVisible.value = true;
};

// ========== 分享功能 ==========
const shareFactoryInst = ref(null);
const shareSet = ref({});
const isShareInitialized = ref(false);

// 初始化分享（只执行一次）
const initShare = (inviteLink) => {
  if (isShareInitialized.value) {
    // 如果已经初始化，只更新链接
    updateShareLink(inviteLink);
    return;
  }

  if (!window.ShareFactory) {
    console.error('ShareFactory 未加载');
    return;
  }

  const shareChannel = [
    { channel: 'Facebook' },
    { channel: 'Line' },
    { channel: 'System' }
  ].filter((item) => item?.channel) || [];

  if (shareChannel.length === 0) {
    return;
  }

  // 分享信息配置
  const shareInfo = {
    appKey: '',
    appId: '1319277882926795',
    aid: window.activity_name || 'row_reservation_01',
    gameId: window.game_id || 386,
    title: '3月26日新版本重磅開啟！',
    content: '和我一起組隊拿積分，抽取驚喜好禮！',
    url: inviteLink || window.location.href,
    shareBtnText: "know more",
    shareImageArr: [],
    shareSdkLoadedCallback: () => {
      console.log('分享 SDK 已加载');
    },
    shareSuccessCallback: async function() {
      console.log('分享成功');

      // 打点上报：分享成功
      const shareType = this.pointName || 'unknown';
      point({
        eventName: 'share_result',
        eventType: shareType,
        eventReason: '分享成功',
        eventValue: {}
      });

      if (window.activityDataLayer) {
        window.activityDataLayer.push({
          eventName: 'share',
          eventType: 'invite',
          aid: window.activity_name,
          gameId: window.game_id,
        });
      }

      // 调用 completeCallback 接口（对应 template_116 的 shareCallback）
      try {
        const shareType = this.pointName || 'invite';
        const url = getActivityApi(window.platform_id, window.activity_name, 'completeCallback');
        const res = await getJSON(url, {
          params: JSON.stringify({
            shareRes: 1,
            shareType: shareType,
            callbackType: shareType,
            shareMsg: ''
          })
        });
        console.log('📦 分享回调响应:', res);

        if (res.result === 1) {
          console.log('✅ 分享回调成功');
        } else {
          console.error('❌ 分享回调失败:', res.msg);
        }
      } catch (error) {
        console.error('❌ 分享回调接口调用失败:', error);
      }

      // 刷新活动数据
      initActivity();
    },
    shareFailCallback: function(errorMsg) {
      console.log('分享失败:', errorMsg);

      // 打点上报：分享失败
      const shareType = this.pointName || 'unknown';
      point({
        eventName: 'share_result',
        eventType: shareType,
        eventReason: '分享失败',
        eventValue: { error: errorMsg || '' }
      });

      // 调用 completeCallback 接口（失败情况）
      (async () => {
        try {
          const shareType = this.pointName || 'invite';
          const url = getActivityApi(window.platform_id, window.activity_name, 'completeCallback');
          await getJSON(url, {
            params: JSON.stringify({
              shareRes: 0,
              shareType: shareType,
              callbackType: shareType,
              shareMsg: errorMsg || ''
            })
          });
        } catch (error) {
          console.error('❌ 分享失败回调接口调用失败:', error);
        }
      })();
    },
  };

  // 创建分享工厂实例
  shareFactoryInst.value = new window.ShareFactory(shareInfo);

  // 创建各平台分享实例（不挂载默认弹窗）
  shareSet.value = {};
  shareChannel.forEach((item) => {
    shareSet.value[item.channel.toLowerCase()] = shareFactoryInst.value.createShareInst({
      type: item.channel,
    });
  });

  isShareInitialized.value = true;
  console.log('分享已初始化');
};

// 触发分享（由自定义弹窗调用）
const triggerShare = (channel = 'facebook') => {
  if (shareSet.value[channel]) {
    shareSet.value[channel].share();
  } else {
    console.error(`分享实例 ${channel} 不存在`);
  }
};

// 更新分享链接
const updateShareLink = (inviteLink) => {
  if (shareFactoryInst.value && inviteLink) {
    shareFactoryInst.value.batchSetUrl(inviteLink);
    console.log('分享链接已更新:', inviteLink);
  }
};

// 获取 shareSet（提供给自定义弹窗使用）
const getShareSet = () => shareSet.value;

// ========== 任务功能 - 简化版（使用原始组件） ==========
// 原始 Task 组件通过 inject 获取所需的状态和方法
// 不需要额外的 state management，由 lotteryAndTaskState 统一管理

// ========== Header 事件处理 ==========
const handleLogout = useDebounceLock(async () => {
  try {
    await getJSON(getLogoutApi(window.platform_id));
    window.location.reload();
  } catch (error) {
    console.error('登出失败:', error);
  }
});

const handleBindRole = () => {
  if (!checkLogin()) {
    gotoLogin();
    return;
  }
  bindRolePopupVisible.value = true;
};

const handleSwitchRole = () => {
  // 检查登录
  if (!checkLogin()) {
    gotoLogin();
    return;
  }

  // 必须已绑定角色才能切换
  if (!isBind.value) {
    showTipMessageOverride('請先綁定角色');
    return;
  }

  // 打开绑定角色弹窗（切换模式，实际就是重新绑定）
  bindRolePopupVisible.value = true;
};

// 绑定成功回调
const handleBindSuccess = useDebounceLock(async (bindData) => {
  console.log('绑定成功:', bindData);

  // 判断是绑定还是切换（通过 isBind 判断）
  const isSwitch = isBind.value;
  showTipMessageOverride(isSwitch ? '切換角色成功' : '綁定成功');

  // 刷新活动数据
  await initActivity();

  // 绑定成功后，检查URL是否有邀请参数并弹窗
  checkAndShowInvitePopup();
});

// 绑定失败回调
const handleBindFailed = (errorCode) => {
  console.log('绑定失败:', errorCode);
};

const handleReserve = useDebounceLock(async () => {
  // 检查登录
  if (!checkLogin()) {
    gotoLogin();
    return;
  }

  // 检查是否绑定角色
  if (!isBind.value) {
    showTipMessageOverride('請先綁定角色');
    // 打开绑定角色弹窗
    bindRolePopupVisible.value = true;
    return;
  }

  try {
    // 调用预约接口
    const res = await getJSON(
      getActivityApi(window.platform_id, window.activity_name, 'doReserve'),
      {}
    );

    if (res.result === 1) {
      showTipMessageOverride('預約成功');
      // 预约成功后刷新活动数据
      await initActivity();
      // 预约成功后，检查URL是否有邀请参数并弹窗
      checkAndShowInvitePopup();
    } else {
      showCurErrorTipsOverride(res?.code);
    }
  } catch (error) {
    console.error('预约失败:', error);
    showTipMessageOverride('預約失敗，請稍後再試');
  }
});

const jumpToFacebook = () => {
  // TODO: 跳转到Facebook
  console.log('跳转到Facebook');
};

const jumpToDiscord = () => {
  // TODO: 跳转到Discord
  console.log('跳转到Discord');
};

// 打开视频弹窗
const showVideoPopup = (url) => {
  videoUrl.value = url;
  videoPopupVisible.value = true;
};

// 打开创建弹窗
const showCreatePopup = () => {
  createPopupVisible.value = true;
};

// 创建弹窗确认
const handleCreateConfirm = useDebounceLock(async () => {
  console.log('创建确认');

  // 调用创建队伍接口
  const success = await triggerCreateTeam(checkLogin, showTipMessageOverride, myCode, getLangTxt);

  if (success) {
    createPopupVisible.value = false;
    // 刷新活动数据
    await initActivity();
  }
});

// 创建弹窗取消
const handleCreateCancel = () => {
  console.log('创建取消');
};

// 打开加入队伍弹窗
const showJoinTeamPopup = () => {
  joinTeamError.value = '';
  joinTeamHasInviteCode.value = false; // 普通模式
  joinTeamPopupVisible.value = true;
};

// 打开加入队伍弹窗（邀请模式）
const showInviteJoinTeamPopup = () => {
  joinTeamError.value = '';
  joinTeamHasInviteCode.value = true; // 邀请模式
  joinTeamPopupVisible.value = true;
};

// 检查URL参数并触发邀请加入队伍弹窗（通用函数）
const checkAndShowInvitePopup = () => {
  // 必须已绑定角色才能弹窗
  if (!isBind.value) {
    console.log('⚠️ 未绑定角色，跳过邀请弹窗');
    return;
  }

  // 必须已预约才能弹窗
  if (!isReserved.value) {
    console.log('⚠️ 未预约，跳过邀请弹窗');
    return;
  }

  // 如果已经有队伍，不弹窗
  if (hasTeam.value) {
    console.log('⚠️ 用户已有队伍，跳过邀请弹窗');
    return;
  }

  // 检查URL参数是否包含邀请码
  const urlParams = new URLSearchParams(window.location.search);
  const urlInviteCode = urlParams.get('inviteCode');
  const urlRoleName = urlParams.get('roleName');

  if (urlInviteCode && urlRoleName) {
    console.log('✅ 检测到URL邀请参数，弹出邀请加入弹窗');
    inviteCode.value = urlInviteCode;
    inviteRoleName.value = decodeURIComponent(urlRoleName);
    showInviteJoinTeamPopup();
  }
};

// 加入队伍确认
const handleJoinTeamConfirm = useDebounceLock(async (teamCode) => {
  console.log('加入队伍，队伍码:', teamCode);

  // 检查登录
  if (!checkLogin()) {
    gotoLogin();
    return;
  }

  try {
    // 调用加入队伍接口
    const result = await joinOtherTeam(teamCode);

    if (result.success) {
      joinTeamPopupVisible.value = false;
      joinTeamError.value = '';
      showTipMessageOverride('加入隊伍成功');
      // 刷新活动数据
      await initActivity();
    } else {
      // 如果有错误码，使用 getLangTxt 显示翻译后的错误信息
      const errorMsg = result.code ? getLangTxt(result.code, getLangTxt('common')) : (result.message || '加入失敗');

      // 如果是邀请模式，用弹窗提示；普通模式则显示在输入框下方
      if (joinTeamHasInviteCode.value) {
        showTipMessageOverride(errorMsg);
        joinTeamPopupVisible.value = false;
      } else {
        joinTeamError.value = errorMsg;
      }
    }
  } catch (error) {
    console.error('加入队伍失败:', error);
    const errorMsg = '加入失敗，請稍後再試';

    // 如果是邀请模式，用弹窗提示；普通模式则显示在输入框下方
    if (joinTeamHasInviteCode.value) {
      showTipMessageOverride(errorMsg);
      joinTeamPopupVisible.value = false;
    } else {
      joinTeamError.value = errorMsg;
    }
  }
});

// 加入队伍取消
const handleJoinTeamCancel = () => {
  console.log('加入队伍取消');
  joinTeamError.value = '';
};

// 打开队伍列表弹窗
const showTeamListPopup = useDebounceLock(async () => {
  // 检查登录
  if (!checkLogin()) {
    gotoLogin();
    return;
  }

  try {
    // 调用获取队伍成员列表接口
    const result = await getTeamMembers();

    if (result.success && result.members) {
      // 映射数据为 TeamListPopup 需要的格式
      teamMembers.value = result.members.map(member => ({
        role: member.role,
        name: member.nickname,
        joinTime: member.joinTime,
      }));
      teamListPopupVisible.value = true;
    } else {
      // 使用 getLangTxt 显示错误信息
      const errorMsg = result.code ? getLangTxt(result.code, getLangTxt('common')) : (result.message || '獲取隊伍列表失敗');
      showTipMessageOverride(errorMsg);
    }
  } catch (error) {
    console.error('获取队伍列表失败:', error);
    showTipMessageOverride(getLangTxt('common'));
  }
});


// ========== Provide 给子组件 ==========

// 抽奖相关
provide('isReady', isReady); // 页面是否已准备完成（用于里程碑等组件）
provide('v3PopupState', v3PopupState);
provide('lotteryResult', lotteryResult);
provide('showCurErrorTips', showCurErrorTipsOverride); // 使用 override 版本
provide('showTipMessage', showTipMessageOverride); // 使用 override 版本
provide('showTips', showTipMessageOverride); // 绑定弹窗使用
provide('checkLogin', checkLogin);
provide('gotoLogin', gotoLogin);
provide('initActivity', initActivity);
provide('refreshTaskStatus', refreshTaskStatus); // 刷新任务状态
provide('lotteryAndTaskState', lotteryAndTaskState);
provide('taskList', taskList); // 任务列表
provide('isLogin', isLogin);
provide('accountName', accountName);
provide('isBind', isBind);
provide('roleName', roleName);
provide('sname', sname);
provide('isReserved', isReserved);
provide('inviteCode', inviteCode);
provide('myCode', myCode);
provide('inviteRoleName', inviteRoleName);
provide('membersCount', membersCount); // 队伍人数
provide('serverTimestamp', serverTimestamp); // 服务器时间戳
provide('init', initLottery);
provide('isMobile', ref(false));
provide('platform', ref(window.platform_id));
provide('gameId', ref(window.game_id));

// 任务相关（原始 Task 组件需要）
provide('activeUgcTab', ref(null));
provide('goUgcList', null);
provide('goCenter', null);
provide('participantCount', ref(0)); // 添加参与人数
provide('claimTaskReward', claimTaskReward); // 领取任务奖励方法

// 分享相关
provide('initShare', initShare);
provide('triggerShare', triggerShare);
provide('updateShareLink', updateShareLink);
provide('getShareSet', getShareSet);

// 规则弹窗相关
provide('showRulePopup', showRulePopup);

// 视频弹窗相关
provide('showVideoPopup', showVideoPopup);

// 创建弹窗相关
provide('showCreatePopup', showCreatePopup);

// 加入队伍弹窗相关
provide('showJoinTeamPopup', showJoinTeamPopup);

// 队伍列表弹窗相关
provide('showTeamListPopup', showTeamListPopup);

// 分享弹窗相关
provide('showSharePopup', showSharePopup);

// 组队状态相关
provide('hasTeam', hasTeam);
provide('teamInviteState', teamInviteState);

// ========== 初始化 ==========
onMounted(async () => {
  // 初始化 globalAdPoint
  initGlobalAdPoint();

  // 应用弹窗缩放(针对小屏幕)
  if (window.innerHeight < 1080) {
    applyPopupScale(0.9);
  }

  await initLottery();

  // 初始化完成后，检查URL是否有邀请参数并弹窗
  checkAndShowInvitePopup();
});
</script>

<style lang="less">
@import url(./assets/less/common.less);
@import url(./assets/less/popup.less);
@import url(./assets/less/header.less);
@import url(./assets/less/milestone.less);
@import url(./assets/less/teamInvite.less);
@import url(./assets/less/lottery.less);
@import url(./assets/less/task.less);
@import url(./assets/less/v3-popup.less);
@import url(./assets/less/app.less);
</style>
